/**
 * SensitivityStore — caches combined adjusted sensitivity API responses per company+range.
 *
 * Prevents redundant API calls when switching away from the Sensitivity tab
 * and returning. Stores the full response, selected range, selected year,
 * and AI webhook insights so the UI restores instantly.
 */

import { create } from 'zustand';
import type { CombinedAdjustedSensitivityResponse } from '../types/financial';

export interface CachedSensitivityEntry {
  data: CombinedAdjustedSensitivityResponse;
  selectedYear: number | string | null;
  selectedRange: string;
  /** AI webhook insights — typed as `unknown` here; the component casts them. */
  icInsights: unknown;
  waccInsights: unknown;
  capInsights: unknown;
}

interface SensitivityState {
  /** Keyed by `${companyName}||${range}` */
  cache: Record<string, CachedSensitivityEntry>;
  /** Last selected range per company (persists across tab switches) */
  selectedRangeByCompany: Record<string, string>;

  getCached: (company: string, range: string) => CachedSensitivityEntry | null;
  setCache: (company: string, range: string, entry: CachedSensitivityEntry) => void;
  getSelectedRange: (company: string) => string | null;
  setSelectedRange: (company: string, range: string) => void;
  updateInsights: (
    company: string,
    range: string,
    insights: Partial<Pick<CachedSensitivityEntry, 'icInsights' | 'waccInsights' | 'capInsights'>>
  ) => void;
  updateSelectedYear: (company: string, range: string, year: number | string | null) => void;
  clearCache: (company: string) => void;
}

const cacheKey = (company: string, range: string) => `${company}||${range}`;

export const useSensitivityStore = create<SensitivityState>((set, get) => ({
  cache: {},
  selectedRangeByCompany: {},

  getCached: (company, range) => get().cache[cacheKey(company, range)] ?? null,

  setCache: (company, range, entry) =>
    set((state) => ({
      cache: { ...state.cache, [cacheKey(company, range)]: entry },
    })),

  getSelectedRange: (company) => get().selectedRangeByCompany[company] ?? null,

  setSelectedRange: (company, range) =>
    set((state) => ({
      selectedRangeByCompany: { ...state.selectedRangeByCompany, [company]: range },
    })),

  updateInsights: (company, range, insights) =>
    set((state) => {
      const key = cacheKey(company, range);
      const existing = state.cache[key];
      if (!existing) return state;
      return {
        cache: {
          ...state.cache,
          [key]: { ...existing, ...insights },
        },
      };
    }),

  updateSelectedYear: (company, range, year) =>
    set((state) => {
      const key = cacheKey(company, range);
      const existing = state.cache[key];
      if (!existing) return state;
      return {
        cache: {
          ...state.cache,
          [key]: { ...existing, selectedYear: year },
        },
      };
    }),

  clearCache: (company) =>
    set((state) => {
      const newCache = { ...state.cache };
      for (const key of Object.keys(newCache)) {
        if (key.startsWith(`${company}||`)) {
          delete newCache[key];
        }
      }
      const { [company]: _, ...restRanges } = state.selectedRangeByCompany;
      return { cache: newCache, selectedRangeByCompany: restRanges };
    }),
}));
